import { useState, useEffect } from 'react';
import { 
  isAuthenticated, 
  getUser, 
  clearAuth, 
  handleMarketplaceRedirect,
  getSubscriptionStatus 
} from '../services/auth';
import { MARKETPLACE_URL } from '../config';

/**
 * MarketplaceAuth - Authentication wrapper for AWS Marketplace customers.
 * 
 * This component:
 * - Checks for JWT token from Marketplace redirect
 * - Validates existing tokens
 * - Provides user context to children
 * - Shows appropriate UI based on auth state
 */
export function MarketplaceAuth({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    setLoading(true);
    setError(null);

    try {
      // Check for token in URL (from Marketplace redirect)
      const redirectHandled = handleMarketplaceRedirect();
      
      if (redirectHandled) {
        console.log('Marketplace redirect handled, token stored');
      }

      // Check if authenticated
      if (isAuthenticated()) {
        const storedUser = getUser();
        setUser(storedUser);

        // Fetch subscription status
        try {
          const status = await getSubscriptionStatus();
          setSubscription(status.subscription);
          
          // Update user with subscription info
          if (status.subscription) {
            setUser(prev => ({
              ...prev,
              ...status.subscription,
            }));
          }
        } catch (err) {
          console.error('Failed to fetch subscription status:', err);
          // Don't fail auth if subscription fetch fails
        }
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setError(err.message);
      clearAuth();
    } finally {
      setLoading(false);
    }
  }

  function handleSignOut() {
    clearAuth();
    setUser(null);
    setSubscription(null);
    // Redirect to home or login page
    window.location.href = '/';
  }

  // Loading state
  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-loading">
            <span className="spinner"></span>
            <p>Verifying session...</p>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated - render children with user context
  if (user) {
    return children({ 
      user, 
      subscription,
      signOut: handleSignOut,
      refreshAuth: checkAuth,
    });
  }

  // Not authenticated - show login options
  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>AI Document <span>Classifier</span></h2>

        <div className="auth-marketplace">
          <p className="auth-description">
            To use this service you need an active subscription on AWS Marketplace.
          </p>

          {error && (
            <div className="auth-error">{error}</div>
          )}

          <a
            href={MARKETPLACE_URL}
            className="btn-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Go to AWS Marketplace
          </a>

          <p className="auth-help">
            Already registered in this app?{' '}
            <button
              type="button"
              className="btn-link"
              onClick={checkAuth}
            >
              Verify session
            </button>
          </p>
          <p className="auth-help" style={{ marginTop: '0.5rem', fontSize: '0.85em' }}>
            Subscribed on AWS Marketplace but haven't set up your account? Use "Go to AWS Marketplace" above and click "Set up your account".
          </p>
        </div>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <div className="auth-demo">
          <p className="auth-description">
            Want to try the demo without a subscription?
          </p>
          <a
            href="/?demo=true"
            className="btn-secondary"
          >
            View Demo
          </a>
        </div>
      </div>
    </div>
  );
}
